import { Assembly, PoiTypeCount, UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import {
  ALARM_LEVEL_MANUALLY_SET_TEMPLATE_KEY,
  ALARM_LEVEL_TEMPLATE_KEY,
  AlarmLevelSettingStatus,
  CHILD_COMPONENTS_KEY,
  COVERAGE_PERCENTAGE,
  COVERAGE_RATING,
  MAX_BLISTER_HEIGHT_TEMPLATE_FIELD_KEY,
  MOUNTED_PIPES_KEY,
  SPAN_DISTANCE_KEY,
} from '@/constants';
import { Columns } from '@/types';
import { formatFieldValue, getCoverageLevel, getPipeSize } from '@/utils';
import { useCallback } from 'react';
import { TableData } from '@/types/table';
import { useGetStructureId } from './useGetStructureId';
import * as analysisState from '@/components/Analysis/state';

export const useEquipmentData = () => {
  const unitSystem = useAtomValue(state.unitSystem);
  const structurePipesByStructureId = useAtomValue(state.structurePipesByStructureId);
  const structureId = useGetStructureId();
  const allPoiTemplates = useAtomValue(analysisState.allPointOfInterestTemplates);

  const getCellValue = useCallback(
    (assembly: TableData, column: Columns, exportXlsx?: boolean | undefined) => {
      const {
        templateFieldKey,
        unit,
        key,
        isTemplateField,
        optionsData,
        displayUnit,
        roundToDecimal,
        isBlisterField,
      } = column;
      const { templateFieldData } = assembly;
      let value;
      let subValue: string | undefined;

      if (key === 'size') {
        value = getPipeSize({
          size: Number(
            assembly.templateFieldData?.[key as keyof typeof assembly.templateFieldData]
          ),
          getUnitSuffix: () => (unitSystem === UnitSystemEnum.Metric ? 'mm' : 'in'),
          unitSystem,
          displayUnit: false,
          structureSteelPipes: structurePipesByStructureId[structureId],
        }) as string;
        return { value, subValue };
      }

      // Handle POI icons column (displays all POI types with icons)
      if (key === 'poiIcons') {
        const poiCounts = (assembly as Assembly).poiCounts;

        if (!poiCounts || !Array.isArray(poiCounts) || poiCounts.length === 0) {
          return { value: JSON.stringify([]), subValue };
        }

        // Return the full poiCounts array as a JSON string for the POI_ICONS cell type

        const templatesByLowerKey = (allPoiTemplates || []).reduce<Record<string, unknown>>(
          (acc, pointTemplate) => {
            const keyVal = (pointTemplate?.key || pointTemplate?.name || '').toString();
            if (keyVal) acc[keyVal.toLowerCase()] = keyVal;
            return acc;
          },
          {} as Record<string, unknown>
        );

        const poiItems = poiCounts.map((poi: PoiTypeCount) => {
          const type = (poi.poiType || '').toString();
          const key = templatesByLowerKey[type.toLowerCase()];
          return {
            ...poi,
            poiTemplateKey: key,
          };
        });

        return { value: JSON.stringify(poiItems), subValue };
      }

      // Handle individual POI count columns (one column per POI type)
      if (key.startsWith('poi_')) {
        const poiType = key.replace('poi_', '');
        const poiCounts = (assembly as Assembly).poiCounts;

        if (!poiCounts || !Array.isArray(poiCounts)) {
          return { value: '0', subValue };
        }

        const poiData = poiCounts.find(
          (poi: { poiType: string; count: number }) => poi.poiType === poiType
        );

        return {
          value: poiData ? poiData.count.toString() : '0',
          subValue,
        };
      }

      if (!isTemplateField || !templateFieldKey || !templateFieldData) {
        value = (assembly as Record<string, unknown>)[key] as string;
        if (value === undefined || value === null || value === '')
          return { value: 'N/A', subValue };
        else return typeof value === 'object' ? { value: 'N/A', subValue } : { value, subValue };
      }
      value = templateFieldData?.[templateFieldKey];

      // Multi-value chip cells receive an array; bypass scalar formatting so
      // CustomCell can render chips. Export paths flatten to a newline-joined
      // string so Excel/CSV stay consistent with the upload convention.
      if (Array.isArray(value) && value.length > 0) {
        if (exportXlsx) return { value: value.join('\n'), subValue };
        return { value, subValue };
      }

      if (templateFieldKey === COVERAGE_RATING) {
        if (templateFieldData?.[COVERAGE_RATING] === undefined) {
          value = getCoverageLevel(templateFieldData?.[COVERAGE_PERCENTAGE], optionsData);
        }
      }
      if (!exportXlsx && column.displayConfig?.type === 'DATE') {
        value =
          templateFieldData?.[templateFieldKey] === ''
            ? 'Add Date'
            : (templateFieldData?.[templateFieldKey] ?? 'Add Date');
      } else if (!exportXlsx && column.displayConfig?.type === 'DROPDOWN') {
        value =
          templateFieldData?.[templateFieldKey] === ''
            ? 'Select'
            : (templateFieldData?.[templateFieldKey] ?? 'Select');
      }
      // Handled for unexpected values (they exist in the database)

      if ([undefined, null, 'null', 'undefined'].includes(value)) return { value: 'N/A', subValue };

      const isBlisterHeight = templateFieldKey === MAX_BLISTER_HEIGHT_TEMPLATE_FIELD_KEY;
      const isBooleanField = templateFieldKey === ALARM_LEVEL_MANUALLY_SET_TEMPLATE_KEY;
      if (isBlisterHeight && [0, '0'].includes(value)) return { value: 'Uncertain', subValue };
      if ([0, '0'].includes(value)) return { value: '0', subValue };

      value =
        formatFieldValue({
          unitSystem,
          type: undefined,
          unit: unit ? (unit as UnitsEnum) : undefined,
          value: value?.toString() ?? 'N/A',
          showUnitLabel: false,
          isBooleanField,
          displayUnit: displayUnit as UnitsEnum,
          roundToDecimal,
          isBlisterField,
          isHeight: isBlisterHeight,
        }) ?? value;
      if (key === CHILD_COMPONENTS_KEY) {
        value = 'View child components';
      }
      if (key === MOUNTED_PIPES_KEY) {
        value = 'View piping';
      }
      if (key === SPAN_DISTANCE_KEY) {
        value = 'View span distance';
      }
      // SubValue is only used for Alarm Level for now that is retrieved from another template field
      if (templateFieldKey === ALARM_LEVEL_TEMPLATE_KEY) {
        const isAlarmLevelManuallySet = templateFieldData?.[ALARM_LEVEL_MANUALLY_SET_TEMPLATE_KEY];
        if (isAlarmLevelManuallySet === true) subValue = AlarmLevelSettingStatus.MANUALLY_ADDED;
        else subValue = AlarmLevelSettingStatus.AUTO_GENERATED;
      }

      return { value, subValue };
    },
    [structureId, structurePipesByStructureId, unitSystem, allPoiTemplates]
  );
  return { getCellValue };
};
